/* eslint-disable complexity */
import { Reducer, combineReducers } from 'redux';
import * as Types from '../../types';
import {
  Message,
  OpenReportAction,
  OpenReportTypes,
  PublicReportingAccount,
  getReadableIdWithDash,
} from '../../types';
import changePasswordReducer from './ChangePassword.reducer';
import loginReducer from './LoginValues.reducer';
import openReportReducer from './OpenReport.reducer';
import phoneNumberLoginReducer from './VaultTalkLoginValues.reducer';

export const initialState = {
  requestOpenPerpetrators: false,
  loading: true,
  notfound: true,
  publicReporterAccount: null,
  perpetrators: '',
  redirect: undefined,
  publicReports: [],
  messages: new Map(),
  translateMessages: false,
  session: '',
  solutionToken: '',
  challenge: { challenge: '', reference: '' },
  userAnswer: '',
  navMenu: {
    isOpen: false,
  },
  isSubmitting: false,
  fileUploadErrors: 0,
  isPostFileUploadSubmitting: false,
  postFileUploadErrors: 0,
  reportJustSubmitted: false,
};

interface ReportState {
  requestOpenPerpetrators: boolean;
  notfound: boolean;
  loading: boolean;
  perpetrators: string;
  redirect?: string;
  publicReporterAccount: PublicReportingAccount | null;
  publicReports: Types.Report[];
  messages: Map<string, Types.Message[]>;
  translateMessages: boolean;
  session: string;
  solutionToken: string;
  challenge: object;
  userAnswer: string;
  navMenu: {
    isOpen: boolean;
  };
  isSubmitting: boolean;
  fileUploadErrors: number;
  isPostFileUploadSubmitting: boolean;
  postFileUploadErrors: number;
  reportJustSubmitted: boolean;
}

export const openReportingReducer: Reducer<ReportState, OpenReportAction> = (
  state: ReportState = initialState,
  action: OpenReportAction,
): ReportState => {
  switch (action.type) {
    case OpenReportTypes.RESET:
      return {
        ...initialState,
        notfound: state.notfound,
        loading: state.loading,
        reportJustSubmitted: state.reportJustSubmitted,
      };
    case OpenReportTypes.CLEAR:
      return { ...state, perpetrators: '' };
    case OpenReportTypes.SELECT_SUBMISSION_TYPE:
      const selectedSubmission = action.payload;
      return {
        ...state,
        requestOpenPerpetrators: Boolean(
          selectedSubmission.requestOpenPerpetrators,
        ),
      };
    case OpenReportTypes.SET_EVENT_PERPETRATORS:
      return {
        ...state,
        perpetrators: action.payload,
      };
    case OpenReportTypes.SET_NOTFOUND:
      return { ...state, notfound: action.payload };
    case OpenReportTypes.SET_LOADING:
      return { ...state, loading: action.payload };
    case OpenReportTypes.ON_SUBMIT:
      const { publicReporterAccount, redirect } = action.payload;
      return {
        ...state,
        publicReporterAccount,
        redirect,
      };
    case OpenReportTypes.FETCH_ALL_PUBLIC:
      return {
        ...state,
        publicReports: action.payload.map((rep) => ({
          ...rep,
          displayId: getReadableIdWithDash(rep.reportId),
        })),
      };
    case OpenReportTypes.MESSAGES:
      const messagesAll = new Map(state.messages);
      messagesAll.set(action.payload.reportId, action.payload.messages);
      return {
        ...state,
        messages: messagesAll,
      };
    case OpenReportTypes.MESSAGE:
      const messages = state.messages.get(action.payload.channel) || [];
      const newState = new Map(state.messages);
      newState.set(action.payload.channel, [
        ...messages.filter((m: Message) => m.id !== action.payload.id),
        action.payload,
      ]);
      return {
        ...state,
        messages: newState,
      };
    case OpenReportTypes.READ_MESSAGE:
      const readMessages = state.messages.get(action.payload.channel) || [];
      const readMessageState = new Map(state.messages);
      readMessageState.set(action.payload.channel, [
        ...readMessages.map((m: Message) =>
          m.id !== action.payload.id
            ? m
            : { ...m, state: action.payload.state },
        ),
      ]);
      return {
        ...state,
        messages: readMessageState,
      };
    case OpenReportTypes.TRANSLATE_MESSAGES:
      return { ...state, translateMessages: action.payload };
    case OpenReportTypes.SET_TRANSLATION_ENABLED:
      return {
        ...state,
        publicReporterAccount: state.publicReporterAccount
          ? {
              ...state.publicReporterAccount,
              user: {
                ...state.publicReporterAccount.user,
                translationEnabled: action.payload,
              },
            }
          : state.publicReporterAccount,
      };
    case OpenReportTypes.SESSION:
      return {
        ...initialState,
        notfound: state.notfound,
        loading: state.loading,
        reportJustSubmitted: state.reportJustSubmitted,
        redirect: state.redirect,
        session: action.payload.token,
        fileUploadErrors: state.fileUploadErrors,
        publicReporterAccount: {
          ...action.payload.account,
          id: '',
          displayName: '',
          rpDisplayName: '',
        },
      };
    case OpenReportTypes.SET_FETCHED_CHALLENGE:
      return {
        ...state,
        challenge: action.payload,
      };
    case OpenReportTypes.SET_SOLUTION_TOKEN:
      return {
        ...state,
        solutionToken: action.payload,
      };
    case OpenReportTypes.SET_USER_ANSWER:
      return {
        ...state,
        userAnswer: action.payload,
      };
    case OpenReportTypes.SET_NAV_MENU_SIZE:
      return {
        ...state,
        navMenu: {
          ...state.navMenu,
          isOpen: action.payload,
        },
      };
    case OpenReportTypes.SET_IS_SUBMITTING:
      return {
        ...state,
        isSubmitting: action.payload,
      };
    case OpenReportTypes.SET_UPLOAD_ERRORS:
      return {
        ...state,
        fileUploadErrors: action.payload,
      };
    case OpenReportTypes.SET_POST_SUBMISSION_UPLOAD_ERRORS:
      return {
        ...state,
        postFileUploadErrors: action.payload,
      };
    case OpenReportTypes.SET_POST_SUBMISSION_IS_SUBMITTING:
      return {
        ...state,
        isPostFileUploadSubmitting: action.payload,
      };
    case OpenReportTypes.SET_REPORT_JUST_SUBMITTED:
      return {
        ...state,
        reportJustSubmitted: action.payload,
      };
    default:
      return state;
  }
};

const combined = combineReducers({
  changePasswordReducer,
  openReportReducer,
  loginReducer,
  phoneNumberLoginValues: phoneNumberLoginReducer,
  openReportingReducer,
});

export default combined;
